import type {
  CalendarEventRecord,
  Deal,
  Task,
} from "@/components/atomic-crm/types";
import { isTaskOverdue } from "@/components/atomic-crm/tasks/taskStats";
import { extractTaskDueTime } from "@/components/atomic-crm/tasks/taskDueDateTime";
import {
  getProjectDeliveryDate,
  getProjectDeliveryUrgency,
} from "@/modules/deals/projectDeliveryDate";
import {
  formatEventTimeLabel,
  formatEventTimeRange,
  formatRemindBeforeLabel,
  getCalendarEntryKind,
  normalizeEventTime,
  type CalendarEntryKind,
} from "@/modules/calendar/calendarReminderOptions";

export type CalendarView = "month" | "week";

export type CalendarDisplayOptions = {
  showSaturday: boolean;
  showSunday: boolean;
};

export const WEEKDAY_SHORT_LABELS = [
  "Su",
  "Mo",
  "Tu",
  "We",
  "Th",
  "Fr",
  "Sa",
] as const;

export const getWeekdayLabel = (date: Date) =>
  WEEKDAY_SHORT_LABELS[date.getDay()];

export const getVisibleWeekdayLabels = (options: CalendarDisplayOptions) =>
  WEEKDAY_SHORT_LABELS.filter((_, index) => {
    if (index === 0 && !options.showSunday) return false;
    if (index === 6 && !options.showSaturday) return false;
    return true;
  });

export const GRID_COLUMN_CLASS: Record<number, string> = {
  5: "grid-cols-5",
  6: "grid-cols-6",
  7: "grid-cols-7",
};

export const isSunday = (date: Date) => date.getDay() === 0;

export const isSaturday = (date: Date) => date.getDay() === 6;

export const isDayVisible = (
  date: Date,
  { showSaturday, showSunday }: CalendarDisplayOptions,
) => {
  if (isSunday(date) && !showSunday) return false;
  if (isSaturday(date) && !showSaturday) return false;
  return true;
};

export const getVisibleColumnCount = (options: CalendarDisplayOptions) =>
  7 - (options.showSunday ? 0 : 1) - (options.showSaturday ? 0 : 1);

/** Weekend columns: one neutral tint for both days (no color coding). */
export const getWeekendCellClassName = (date: Date) =>
  isSunday(date) || isSaturday(date) ? "bg-muted/40" : "";

export type CalendarEvent =
  | {
      kind: "task";
      id: string;
      date: string;
      time?: string | null;
      title: string;
      task: Task;
      overdue: boolean;
      done: boolean;
    }
  | {
      kind: "project_delivery";
      id: string;
      date: string;
      title: string;
      dealId: Deal["id"];
      dealName: string;
      /** Delivery date passed and the project is not delivered. */
      overdue?: boolean;
    }
  | {
      kind: "project_start";
      id: string;
      date: string;
      title: string;
      dealId: Deal["id"];
      dealName: string;
    }
  | {
      kind: CalendarEntryKind;
      id: string;
      date: string;
      time?: string | null;
      title: string;
      record: CalendarEventRecord;
      done: boolean;
      assignedName?: string | null;
      contactName?: string | null;
    };

export const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Next 5-minute boundary from now, for instant meeting scheduling. */
export const roundToNextFiveMinutes = (date = new Date()) => {
  const next = new Date(date);
  const minutes = next.getMinutes();
  const remainder = minutes % 5;
  const addMinutes =
    remainder === 0 && next.getSeconds() === 0 ? 0 : 5 - remainder;
  next.setMinutes(minutes + addMinutes, 0, 0);
  const hours = String(next.getHours()).padStart(2, "0");
  const mins = String(next.getMinutes()).padStart(2, "0");
  return `${hours}:${mins}:00`;
};

export const parseDateKey = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const addDays = (date: Date, amount: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return startOfDay(next);
};

export const addMonths = (date: Date, amount: number) =>
  new Date(date.getFullYear(), date.getMonth() + amount, 1);

export const startOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

export const endOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0);

export const startOfWeek = (date: Date) => {
  const start = startOfDay(date);
  start.setDate(start.getDate() - start.getDay());
  return start;
};

export const endOfWeek = (date: Date) => addDays(startOfWeek(date), 6);

export const isSameDay = (left: Date, right: Date) =>
  toDateKey(left) === toDateKey(right);

export const isSameMonth = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth();

export const formatMonthLabel = (date: Date) =>
  date.toLocaleDateString(undefined, { month: "short", year: "numeric" });

export const formatWeekLabel = (date: Date) => {
  const start = startOfWeek(date);
  const end = endOfWeek(date);
  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();

  if (sameMonth) {
    const month = start.toLocaleDateString(undefined, { month: "short" });
    return `${month} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
  }

  if (sameYear) {
    const startLabel = start.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
    const endLabel = end.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
    return `${startLabel}–${endLabel}, ${start.getFullYear()}`;
  }

  const startLabel = start.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
  const endLabel = end.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
  return `${startLabel}–${endLabel}`;
};

export const formatDayLabel = (dateKey: string) =>
  parseDateKey(dateKey).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export const getMonthGridDays = (anchor: Date) => {
  const monthStart = startOfMonth(anchor);
  const monthEnd = endOfMonth(anchor);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = addDays(startOfWeek(monthEnd), 6);
  const days: Date[] = [];
  let cursor = gridStart;
  while (cursor <= gridEnd) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return days;
};

export const getWeekDays = (anchor: Date) =>
  Array.from({ length: 7 }, (_, index) => addDays(startOfWeek(anchor), index));

export const normalizeDateOnly = (value?: string | null) => {
  if (!value?.trim()) return null;
  return value.length >= 10 ? value.slice(0, 10) : null;
};

export const buildTaskCalendarEvent = (task: Task): CalendarEvent | null => {
  const date = normalizeDateOnly(task.due_date);
  if (!date) return null;

  return {
    kind: "task",
    id: `task:${task.id}`,
    date,
    time: extractTaskDueTime(task.due_date),
    title: task.text?.trim() || "Task",
    task,
    overdue: isTaskOverdue(task),
    done: Boolean(task.done_date),
  };
};

export const buildDealCalendarEvents = (deal: Deal): CalendarEvent[] => {
  const events: CalendarEvent[] = [];
  const dealName = deal.name?.trim() || `Project #${deal.id}`;

  const deliveryDate = normalizeDateOnly(getProjectDeliveryDate(deal));
  if (deliveryDate) {
    events.push({
      kind: "project_delivery",
      id: `delivery:${deal.id}`,
      date: deliveryDate,
      title: dealName,
      dealId: deal.id,
      dealName,
      overdue:
        getProjectDeliveryUrgency(deliveryDate, { stage: deal.stage }) ===
        "overdue",
    });
  }

  const startDate = normalizeDateOnly(deal.start_date);
  if (startDate) {
    events.push({
      kind: "project_start",
      id: `start:${deal.id}`,
      date: startDate,
      title: dealName,
      dealId: deal.id,
      dealName,
    });
  }

  return events;
};

export const buildCalendarEntryEvent = (
  record: CalendarEventRecord,
  names?: { assignedName?: string | null; contactName?: string | null },
): CalendarEvent | null => {
  const date = normalizeDateOnly(record.event_date);
  if (!date) return null;

  const kind = getCalendarEntryKind(record);

  return {
    kind,
    id: `${kind}:${record.id}`,
    date,
    time: normalizeEventTime(record.event_time),
    title: record.title?.trim() || "Reminder",
    record,
    done: Boolean(record.completed_at),
    assignedName: names?.assignedName ?? null,
    contactName: names?.contactName ?? null,
  };
};

export const groupEventsByDate = (events: CalendarEvent[]) => {
  const grouped: Record<string, CalendarEvent[]> = {};
  events.forEach((event) => {
    grouped[event.date] = grouped[event.date]
      ? [...grouped[event.date], event]
      : [event];
  });
  Object.values(grouped).forEach((entries) => {
    entries.sort((left, right) => {
      const kindOrder = (event: CalendarEvent) => {
        if (event.kind === "task" && event.overdue && !event.done) return 0;
        if (event.kind === "task") return 1;
        if (event.kind === "activity") return 2;
        if (event.kind === "meeting") return 2;
        if (event.kind === "scheduled_task") return 3;
        if (event.kind === "reminder") return 4;
        if (event.kind === "project_delivery") return 5;
        return 6;
      };
      const orderDiff = kindOrder(left) - kindOrder(right);
      if (orderDiff !== 0) return orderDiff;
      const leftTime = "time" in left ? (left.time ?? "99:99") : "99:99";
      const rightTime = "time" in right ? (right.time ?? "99:99") : "99:99";
      const timeDiff = leftTime.localeCompare(rightTime);
      if (timeDiff !== 0) return timeDiff;
      return left.title.localeCompare(right.title);
    });
  });
  return grouped;
};

export const getVisibleRange = (
  anchor: Date,
  view: CalendarView,
  options?: { expandMonths?: number },
) => {
  let start: Date;
  let end: Date;

  if (view === "week") {
    start = startOfWeek(anchor);
    end = endOfWeek(anchor);
  } else {
    const days = getMonthGridDays(anchor);
    start = days[0];
    end = days[days.length - 1];
  }

  const expandMonths = options?.expandMonths ?? 0;
  if (expandMonths > 0) {
    start = addMonths(start, -expandMonths);
    end = addMonths(end, expandMonths);
  }

  return { start: toDateKey(start), end: toDateKey(end) };
};

export const getEventClassName = (event: CalendarEvent) => {
  if (event.kind === "task") {
    if (event.done) {
      return "bg-muted text-muted-foreground line-through";
    }
    if (event.overdue) {
      return "bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-100";
    }
    return "bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:text-blue-100";
  }
  if (event.kind === "project_delivery") {
    // A late delivery reads red, like an overdue task.
    if (event.overdue) {
      return "bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-100";
    }
    return "bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-100";
  }
  if (event.kind === "activity") {
    if (event.done) {
      return "bg-muted text-muted-foreground line-through";
    }
    return "bg-amber-50 text-amber-950 dark:bg-amber-950/40 dark:text-amber-100";
  }
  if (event.kind === "meeting") {
    if (event.done) {
      return "bg-muted text-muted-foreground line-through";
    }
    return "bg-emerald-50 text-emerald-950 dark:bg-emerald-950/40 dark:text-emerald-100";
  }
  if (event.kind === "scheduled_task") {
    if (event.done) {
      return "bg-muted text-muted-foreground line-through";
    }
    return "bg-sky-50 text-sky-950 dark:bg-sky-950/40 dark:text-sky-100";
  }
  if (event.kind === "reminder") {
    if (event.done) {
      return "bg-muted text-muted-foreground line-through";
    }
    return "bg-yellow-50 text-yellow-950 dark:bg-yellow-950/40 dark:text-yellow-100";
  }
  return "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100";
};

const GENERIC_EVENT_TITLES = new Set([
  "reminder",
  "task",
  "activity",
  "meeting",
  "follow up",
  "follow-up",
  "followup",
  "event",
  "scheduled task",
  "scheduled",
]);

const isGenericEventTitle = (title: string | null | undefined) => {
  const normalized = title?.trim().toLowerCase() ?? "";
  return !normalized || GENERIC_EVENT_TITLES.has(normalized);
};

const truncatePreviewText = (text: string, maxLength = 52) => {
  const trimmed = text.trim();
  if (!trimmed) return "";
  return trimmed.length > maxLength
    ? `${trimmed.slice(0, maxLength - 1)}…`
    : trimmed;
};

const firstLineOfDescription = (
  description: string | null | undefined,
  maxLength = 52,
) => {
  const line = description?.split(/\r?\n/)[0]?.trim() ?? "";
  if (!line) return null;
  return truncatePreviewText(line, maxLength);
};

const textIncludesName = (text: string, name: string) =>
  text.toLowerCase().includes(name.toLowerCase());

const getCalendarEntryDisplayTime = (
  event: Extract<
    CalendarEvent,
    { kind: "activity" | "meeting" | "scheduled_task" | "reminder" }
  >,
) => {
  const timeLabel = formatEventTimeLabel(event.time);
  const timeRangeLabel = formatEventTimeRange(
    event.record.event_time,
    event.record.duration_minutes,
  );
  return timeRangeLabel ?? timeLabel;
};

/** What the event is about — shown first on chips so truncation keeps the useful part. */
const buildCalendarEntryChipPreview = (
  event: Extract<
    CalendarEvent,
    { kind: "activity" | "meeting" | "scheduled_task" | "reminder" }
  >,
) => {
  const title = event.title?.trim() ?? "";
  const descriptionLine = firstLineOfDescription(event.record.description);
  const contactName = event.contactName?.trim();
  const assignedName = event.assignedName?.trim();
  const parts: string[] = [];

  if (title && !isGenericEventTitle(title)) {
    parts.push(truncatePreviewText(title));
  } else if (descriptionLine) {
    parts.push(descriptionLine);
  } else if (title) {
    parts.push(title);
  }

  if (
    contactName &&
    !parts.some((part) => textIncludesName(part, contactName))
  ) {
    parts.push(contactName);
  }

  if (parts.length === 0 && descriptionLine) {
    parts.push(descriptionLine);
  }

  if (parts.length === 0 && assignedName) {
    parts.push(assignedName);
  }

  if (parts.length === 0) {
    parts.push("Scheduled");
  }

  return parts.join(" · ");
};

/** Compact label for calendar grid chips — preview first, type conveyed by chip color. */
export const getEventChipLabel = (event: CalendarEvent) => {
  if (event.kind === "task") {
    const shortTime = formatEventTimeLabel(event.time);
    return shortTime ? `${event.title} · ${shortTime}` : event.title;
  }
  if (event.kind === "project_delivery" || event.kind === "project_start") {
    return event.title;
  }
  if (
    event.kind === "activity" ||
    event.kind === "meeting" ||
    event.kind === "scheduled_task" ||
    event.kind === "reminder"
  ) {
    const preview = buildCalendarEntryChipPreview(event);
    const shortTime = formatEventTimeLabel(event.time);
    return shortTime ? `${preview} · ${shortTime}` : preview;
  }
  return event.title;
};

/** Full hover text for truncated chips. */
export const getEventChipTooltip = (event: CalendarEvent) => {
  if (event.kind === "task") {
    const timeLabel = formatEventTimeLabel(event.time);
    const startKey = event.task.start_date?.slice(0, 10);
    const startedLabel =
      startKey && startKey < event.date
        ? `Started ${parseDateKey(startKey).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}`
        : null;
    return [event.title, timeLabel, startedLabel].filter(Boolean).join(" · ");
  }
  if (event.kind === "project_delivery" || event.kind === "project_start") {
    return event.title;
  }
  if (
    event.kind === "activity" ||
    event.kind === "meeting" ||
    event.kind === "scheduled_task" ||
    event.kind === "reminder"
  ) {
    const preview = buildCalendarEntryChipPreview(event);
    const displayTime = getCalendarEntryDisplayTime(event);
    const descriptionLine = firstLineOfDescription(
      event.record.description,
      120,
    );
    const details = [preview, displayTime, descriptionLine]
      .filter(Boolean)
      .filter((value, index, array) => array.indexOf(value) === index);
    return details.join(" · ");
  }
  return getEventChipLabel(event);
};

export const getEventLabel = (event: CalendarEvent) => {
  if (event.kind === "task") return event.title;
  if (
    event.kind === "activity" ||
    event.kind === "meeting" ||
    event.kind === "scheduled_task" ||
    event.kind === "reminder"
  ) {
    const timeLabel = formatEventTimeLabel(event.time);
    const timeRangeLabel =
      "record" in event
        ? formatEventTimeRange(
            event.record.event_time,
            event.record.duration_minutes,
          )
        : null;
    const displayTime = timeRangeLabel ?? timeLabel;
    const prefix =
      event.kind === "meeting"
        ? "Meeting"
        : event.kind === "activity"
          ? "Activity"
          : event.kind === "scheduled_task"
            ? "Task"
            : "Reminder";
    const linkedName = event.contactName || event.assignedName;
    const titleParts = [prefix, displayTime, event.title].filter(Boolean);
    const base = titleParts.join(" · ");
    return linkedName ? `${base} · ${linkedName}` : base;
  }
  if (event.kind === "project_delivery") {
    return event.overdue
      ? `Delivery (overdue) · ${event.title}`
      : `Delivery · ${event.title}`;
  }
  return `Start · ${event.title}`;
};

export const getCalendarEntryMeta = (
  event: Extract<
    CalendarEvent,
    { kind: "activity" | "meeting" | "scheduled_task" | "reminder" }
  >,
) => {
  const timeLabel = formatEventTimeLabel(event.time);
  const remindLabel = formatRemindBeforeLabel(
    event.record.remind_before_minutes,
  );
  return { timeLabel, remindLabel };
};

export type CalendarEntryEvent = Extract<
  CalendarEvent,
  { kind: "activity" | "meeting" | "scheduled_task" | "reminder" }
>;

export const isCalendarEntryEvent = (
  event: CalendarEvent,
): event is CalendarEntryEvent =>
  event.kind === "activity" ||
  event.kind === "meeting" ||
  event.kind === "scheduled_task" ||
  event.kind === "reminder";
